import React, { useEffect, useState, useRef } from "react";
import Classes from "./Classes";
import toast, { Toaster } from "react-hot-toast";
import "./addNewCourse.css";
import { getCoachesList } from "../../API/coach-mgmt";
import { IKContext, IKUpload } from "imagekitio-react";
import { imageKitPublicKey, imageKitUrlEndpoint, uri } from "../../helper";
import { getClasses, createClass, updateClass } from "../../API/classes";
import TestQuestions from "./TestQuestions";

interface AddNewCourseProps {
  handleClose: () => void;
  data?: any | undefined;
  changeIsActive: boolean;
}

export default function AddNewCourse(props: AddNewCourseProps) {
  const [status, setStatus] = useState<boolean>(props.data?.status || false);
  const [courseCategory, setCourseCategory] = useState("");
  const [courseName, setCourseName] = useState(""); // Replaced subCategory with courseName
  const [coach, setCoach] = useState("");
  const [ageGroup, setAgeGroup] = useState("");
  const [days, setDays] = useState("");
  const [duration, setDuration] = useState("");
  const [date, setDate] = useState("");
  const [overview, setOverview] = useState("");
  const [classStructure, setClassStructure] = useState("");
  const [thumbnail, setThumbnail] = useState("");
  const [coachList, setCoachList] = useState<any[]>([]);
  const [allClasses, setAllClasses] = useState<any[]>([]);
  const [testQuestions, setTestQuestions] = useState<any[]>([]);
  const uploadRef = useRef<any>(null);

  const handleClick = () => {
    props.handleClose();
  };

  useEffect(() => {
    getFirstData();
    if (props.data) {
      // Populate form with existing data for editing
      setCourseCategory(props.data.category || "");
      setCourseName(props.data.name?.split(" by ")[0] || "");
      setCoach(props.data.author || "");
      setAgeGroup(`${props.data.minAge}-${props.data.maxAge}` || "");
      setThumbnail(props.data.tumbnailImageUrl || "");
      setStatus(props.data.status || false);
      setAllClasses(props.data.allClasses || []);
      setTestQuestions(props.data.testQuestions || []);
      setOverview(props.data.overview || "");
      setClassStructure(props.data.classStructure?.join("\n") || "");
    }
  }, [props.data]);

  

  const getFirstData = () => {
    getCoachesList().then((data) => {
      setCoachList(data);
    });
  };

  const handleAddClass = (newClass: any) => {
    setAllClasses((prev) => [...prev, newClass]);
  };

  const handleAddTestQuestion = (newQuestion: any) => {
    setTestQuestions((prev) => [...prev, newQuestion]);
  };

  const handleSubmit = async () => {
    if (!courseCategory) {
      toast.error("Please select a course category.");
      return;
    }
    if (!courseName.trim()) { // Updated validation
      toast.error("Please enter a course name.");
      return;
    }
    if (!coach) {
      toast.error("Please select a coach.");
      return;
    }
    if (!ageGroup) {
      toast.error("Please select an age group.");
      return;
    }
    if (!overview.trim()) {
      toast.error("Please provide a course overview.");
      return;
    }
    if (!classStructure.trim()) {
      toast.error("Please provide a class structure.");
      return;
    }
    if (!thumbnail) {
      toast.error("Please upload a thumbnail image.");
      return;
    }
    if (allClasses.length === 0) {
      toast.error("Please add at least one class.");
      return;
    }
    if (testQuestions.length === 0) {
      toast.error("Please add at least one test question.");
      return;
    }

    const [minAge, maxAge] = ageGroup.split("-").map(Number) || [0, 0];
    if (isNaN(minAge) || isNaN(maxAge) || minAge >= maxAge) {
      toast.error("Invalid age group range.");
      return;
    }

    const classData = {
      name: `${courseName} by ${coach}`, // Updated to use courseName
      overview,
      minAge,
      maxAge,
      tumbnailImageUrl: thumbnail,
      author: coach,
      category: courseCategory,
      startDateTime: date ? new Date(date).toISOString() : undefined,
      classStructure: classStructure.split("\n").filter(Boolean),
      allClasses,
      testQuestions,
    };

    try {
      if (props.data?._id) {
        // Update existing course
        await updateClass(props.data._id, classData);
        toast.success("Course updated successfully!");
      } else {
        // Create new course
        await createClass(classData);
        toast.success("Course created successfully!");
      }
      setTimeout(() => { props.handleClose(); }, 1500)
    } catch (e) {
      toast.error("Failed to create class. Please try again.");
      console.error("Failed to create class:", e);
    }
  };

  const onError = (e: any) => {
    toast.error("Thumbnail upload failed.");
    
  };

  const handleDeleteClass = (index: number) => {
    setAllClasses((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDeleteTestQuestion = (index: number) => {
    setTestQuestions((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <>
      <Toaster
        toastOptions={{
          style: { color: "black" },
        }}
      />
      <div className="addNew-Course">
        <div className="fullDesc-addNewCourse">
          <div className="headerofANC">
            <h3>Add New Knowledge</h3>
            <div
              className="top-svg"
              onClick={handleClick}
              style={{ cursor: "pointer" }}
            >
              <svg
                width="30"
                height="30"
                viewBox="0 0 30 30"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15 0C18.9782 0 22.7936 1.58035 25.6066 4.3934C28.4196 7.20644 30 11.0218 30 15C30 18.9782 28.4196 22.7936 25.6066 25.6066C22.7936 28.4196 18.9782 30 15 30C11.0218 30 7.20644 28.4196 4.3934 25.6066C1.58035 22.7936 0 18.9782 0 15C0 11.0218 1.58035 7.20644 4.3934 4.3934C7.20644 1.58035 11.0218 0 15 0ZM15 13.1807L11.5157 9.69643C11.2745 9.45518 10.9473 9.31964 10.6061 9.31964C10.2649 9.31964 9.93768 9.45518 9.69643 9.69643C9.45518 9.93768 9.31964 10.2649 9.31964 10.6061C9.31964 10.9473 9.45518 11.2745 9.69643 11.5157L13.1807 15L9.69643 18.4843C9.57697 18.6037 9.48221 18.7456 9.41757 18.9016C9.35292 19.0577 9.31964 19.225 9.31964 19.3939C9.31964 19.5629 9.35292 19.7301 9.41757 19.8862C9.48221 20.0423 9.57697 20.1841 9.69643 20.3036C9.81588 20.423 9.9577 20.5178 10.1138 20.5824C10.2699 20.6471 10.4371 20.6804 10.6061 20.6804C10.775 20.6804 10.9423 20.6471 11.0984 20.5824C11.2544 20.5178 11.3963 20.423 11.5157 20.3036L15 16.8193L18.4843 20.3036C18.6037 20.423 18.7456 20.5178 18.9016 20.5824C19.0577 20.6471 19.225 20.6804 19.3939 20.6804C19.5629 20.6804 19.7301 20.6471 19.8862 20.5824C20.0423 20.5178 20.1841 20.423 20.3036 20.3036C20.423 20.1841 20.5178 20.0423 20.5824 19.8862C20.6471 19.7301 20.6804 19.5629 20.6804 19.3939C20.6804 19.225 20.6471 19.0577 20.5824 18.9016C20.5178 18.7456 20.423 18.6037 20.3036 18.4843L16.8193 15L20.3036 11.5157C20.423 11.3963 20.5178 11.2544 20.5824 11.0984C20.6471 10.9423 20.6804 10.775 20.6804 10.6061C20.6804 10.4371 20.6471 10.2699 20.5824 10.1138C20.5178 9.9577 20.423 9.81588 20.3036 9.69643C20.1841 9.57697 20.0423 9.48221 19.8862 9.41757C19.7301 9.35292 19.5629 9.31964 19.3939 9.31964C19.225 9.31964 19.0577 9.35292 18.9016 9.41757C18.7456 9.48221 18.6037 9.57697 18.4843 9.69643L15 13.1807Z"
                  fill="#8E8EA1"
                />
              </svg>


            </div>
          </div>
          <div className="acard-border-top"></div>
          <div className="ac-body">
            <div className="first-drop-line">
              <div className="dropdown">
                <label className="cat-hello" htmlFor="cat">
                  Course Category
                </label>
                <select
                  name="course-category"
                  value={courseCategory}
                  onChange={(e) => setCourseCategory(e.target.value)}
                  className="form-select setThird setBatchThird"
                >
                  <option value="">Select category</option>
                  <option value="Music">Music</option>
                  <option value="Math">Math</option>
                  <option value="Dance">Dance</option>
                  <option value="Martial Art">Martial Art</option>
                  <option value="Art">Art</option>
                </select>
              </div>
              <div className="next-value">
                <div className="dropdown">
                  <label className="cat-hello" htmlFor="course-name">
                    Name
                  </label>
                  <input
                    type="text"
                    className="form-control setThird setBatchThird"
                    name="course-name"
                    value={courseName}
                    onChange={(e) => setCourseName(e.target.value)}
                    placeholder="Enter course name"
                  />
                </div>
              </div>
              <div className="next-value">
                <div className="dropdown">
                  <label className="cat-hello" htmlFor="cat">
                    Coach/Mentor
                  </label>
                  <select
                    className="form-select setThird setBatchThird"
                    name="coach/mentor"
                    value={coach}
                    onChange={(e) => setCoach(e.target.value)}
                  >
                    <option value="">Select Coach</option>
                    {coachList.map((coachItem, index) => (
                      <option key={index} value={coachItem.name}>
                        {coachItem.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            {/* Rest of the code remains unchanged */}
            <div className="second-drop-line">
              <div className="dropdown">
                <label className="cat-hello" htmlFor="cat">
                  Age Group
                </label>
                <select
                  name="age-group"
                  value={ageGroup}
                  onChange={(e) => setAgeGroup(e.target.value)}
                  className="form-select setThird setBatchThird"
                >
                  <option value="">Select age group</option>
                  <option value="3-6">3 - 6</option>
                  <option value="6-9">6 - 9</option>
                  <option value="3-9">3 - 9</option>
                </select>
              </div>
              <div className="next-value">
                <div className="dropdown">
                  <label className="cat-hello" htmlFor="cat">
                    Days
                  </label>
                  <select
                    className="form-select setThird setBatchThird"
                    name="days"
                    value={days}
                    onChange={(e) => setDays(e.target.value)}
                  >
                    <option value="">Select days</option>
                    <option value="Mon-Wed-Fri">Mon-Wed-Fri</option>
                    <option value="Tue-Thu-Sat">Tue-Thu-Sat</option>
                    <option value="Weekends">Weekends</option>
                  </select>
                </div>
              </div>
              <div className="next-value">
                <div className="dropdown">
                  <label className="cat-hello" htmlFor="cat">
                    Duration
                  </label>
                  <select
                    className="form-select setThird setBatchThird"
                    name="duration"
                    value={duration}
                    onChange={(e) => setDuration(e.target.value)}
                  >
                    <option value="">Select duration</option>
                    <option value="30min">30 minutes</option>
                    <option value="45min">45 minutes</option>
                    <option value="1hr">1 hour</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="third-drop-line">
              <div className="dropdown">
                <label className="cat-hello" htmlFor="cat">
                  Date (Optional)
                </label>
                <input
                  type="date"
                  className="form-select setThird setBatchThird"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </div>
              <div className="next-value">
                <div className="mx-3">
                  <label className="cat-hello mx-1" htmlFor="cat">
                    Mode
                  </label>
                  <div className="radio-child">
                    <label className="align-label px-2">
                      <input
                        type="radio"
                        name="choice3"
                        checked={status}
                        onChange={() => setStatus(true)}
                      />
                      <span className="mx-2">Online</span>
                    </label>
                    <label className="align-label px-4">
                      <input
                        type="radio"
                        name="choice3"
                        checked={!status}
                        onChange={() => setStatus(false)}
                      />
                      <span className="mx-2">Offline</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="ac-body">
            <div className="overview-structure">
              <div className="dropdown-new">
                <label className="cat-hello" htmlFor="cname">
                  Overview
                </label>
                <textarea
                  name="overview"
                  className="setOverviewDesc form-control"
                  value={overview}
                  onChange={(e) => setOverview(e.target.value)}
                  placeholder="Enter course overview"
                />
              </div>

              <div className="dropdown-new">
                <label className="cat-hello" htmlFor="cname">
                  Class Structure
                </label>
                <textarea
                  name="class-structure"
                  className="setOverviewDesc form-control"
                  value={classStructure}
                  onChange={(e) => setClassStructure(e.target.value)}
                  placeholder="Enter class structure (one per line)"
                />
              </div>

            </div>
            <div className="next-step-value">
              <div className="dropdown-new">
                <label className="cat-hello" htmlFor="cname">
                  Upload Thumbnail
                </label>
                <div className="uplode-image-video">
                  <IKContext
                    publicKey={imageKitPublicKey}
                    urlEndpoint={imageKitUrlEndpoint}
                    authenticationEndpoint={uri + `/challenge/auth`}
                  >
                    <IKUpload
                      className="ik-upload-button"
                      fileName={`thumbnail-${Date.now()}`}
                      onError={onError}
                      onSuccess={(res: any) => setThumbnail(res.url)}
                    />
                    {thumbnail && (
                      <span className="set-box-upload">{thumbnail}</span>
                    )}
                  </IKContext>
                </div>
              </div>
            </div>
          </div>
          <div className="classandquestion">
            <Classes
              allClasses={allClasses}
              onAddClass={handleAddClass}
              onEditClass={(index, updatedClass) => {
                const updatedClasses = [...allClasses];
                updatedClasses[index] = updatedClass;
                setAllClasses(updatedClasses);
              }}
              onDeleteClass={handleDeleteClass} // New prop
            />
            <TestQuestions
              testQuestions={testQuestions}
              onAddTestQuestion={handleAddTestQuestion}
              onEditTestQuestion={(index, updatedQuestion) => {
                const updatedQuestions = [...testQuestions];
                updatedQuestions[index] = updatedQuestion;
                setTestQuestions(updatedQuestions);
              }}
              onDeleteTestQuestion={handleDeleteTestQuestion} // New prop
            />
          </div>

          <div className="drag-drop-top-line"></div>
          <div className="down-buttons d-flex">
            <button className="create-button" onClick={handleSubmit}>
              Submit
            </button>
            <button className="cancel-button" onClick={handleClick}>
              Cancel
            </button>
          </div>
        </div>
      </div >
    </>
  );
}